import {
  IAcademicsMaterials,
  MaterialCategory,
  MaterialCondition,
  MaterialStatus,
  LabEquipmentType,
  DigitalResourceType,
  StationeryType,
  AccessLevel,
  LicenseType,
} from '../../apis/academics/academicsMaterials.interfaces';
import { mockDocument } from '../documents/documentMock';
import { mockUserAction } from '../users/userAction';

export const mockAcademicsMaterials: IAcademicsMaterials[] = [
  {
    id: '1',
    materials: [
      {
        id: 'b1',
        type: 'book',
        name: 'Chemistry Fundamentals',
        description: 'Comprehensive chemistry textbook',
        category: MaterialCategory.BOOK,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.GOOD,
        purchaseDate: new Date('2023-01-15'),
        location: 'Library Section A',
        quantity: 5,
        minimumQuantity: 2,
        supplier: 'Academic Press',
        cost: 79.99,
        tags: ['chemistry', 'textbook', 'science'],
        actions: {
          created: mockUserAction,
        },
        isbn: '978-0123456789',
        author: 'Dr. Jane Smith',
        publisher: 'Academic Press',
        edition: '3rd',
        publicationYear: 2023,
        subject: 'Chemistry',
        gradeLevel: '11th Grade',
        language: 'English',
        isDigital: false,
      },
      {
        id: 'b2',
        type: 'book',
        name: 'Advanced Physics',
        description: 'Comprehensive physics textbook for advanced students',
        category: MaterialCategory.BOOK,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.GOOD,
        purchaseDate: new Date('2023-01-20'),
        location: 'Physics Department Library',
        quantity: 8,
        minimumQuantity: 3,
        supplier: 'Physics Publishers Inc',
        cost: 89.99,
        tags: ['physics', 'advanced', 'textbook'],
        actions: {
          created: mockUserAction,
        },
        isbn: '978-0987654321',
        author: 'Prof. John Davis',
        publisher: 'Math Publishers Inc',
        edition: '4th',
        publicationYear: 2023,
        subject: 'Physics',
        gradeLevel: '12th Grade',
        language: 'English',
        isDigital: true,
        digitalFormat: 'pdf',
        digitalUrl: 'https://school.digital-library.edu/math-advanced',
      },
      {
        id: 'b3',
        type: 'book',
        name: 'Advanced Chemistry',
        description: 'Comprehensive chemistry textbook for advanced students',
        category: MaterialCategory.BOOK,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.GOOD,
        purchaseDate: new Date('2023-01-20'),
        location: 'Chemistry Department Library',
        quantity: 8,
        minimumQuantity: 3,
        supplier: 'Chemistry Publishers Inc',
        cost: 89.99,
        tags: ['chemistry', 'advanced', 'textbook'],
        actions: {
          created: mockUserAction,
        },
        isbn: '978-0987654321',
        author: 'Prof. John Davis',
        publisher: 'Math Publishers Inc',
        edition: '4th',
        publicationYear: 2023,
        subject: 'Mathematics',
        gradeLevel: '12th Grade',
        language: 'English',
        isDigital: true,
        digitalFormat: 'pdf',
        digitalUrl: 'https://school.digital-library.edu/math-advanced',
      },
    ],
    totalCount: 25,
    availableCount: 15,
    inUseCount: 5,
    maintenanceCount: 2,
    retiredCount: 3,
    totalValue: 12500.75,
    lastInventoryDate: new Date('2023-06-01'),
    department: 'Science',
    location: 'Building A',
    notes: 'Regular inventory checks required',
    name: 'Science Textbooks',
    description: 'Science textbooks for science students',
    documents: {
      receipts: [mockDocument],
      invoices: [mockDocument],
      payments: [mockDocument],
    },
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: new Date(),
    quantity: 0,
    minimumQuantity: 5,
    cost: 75.0,
    tags: ['textbooks', 'science', 'books'],
    actions: {
      created: mockUserAction,
    },
    assignedTo: { studentIds: ['child-1', 'child-2'], classIds: ['class-1'] },
  },
  {
    id: '2',
    materials: [
      {
        id: 'le1',
        type: 'lab_equipment',
        name: 'Advanced Microscope',
        description: 'High-resolution digital microscope for biology lab',
        category: MaterialCategory.LAB_EQUIPMENT,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.LIKE_NEW,
        purchaseDate: new Date('2023-03-10'),
        location: 'Biology Lab 101',
        quantity: 2,
        minimumQuantity: 1,
        supplier: 'LabTech Solutions',
        cost: 2499.99,
        tags: ['microscope', 'biology', 'digital'],
        actions: {
          created: mockUserAction,
        },
        equipmentType: LabEquipmentType.MICROSCOPE,
        modelNumber: 'MT-2000X',
        manufacturer: 'LabTech',
        serialNumber: 'MT2023-001',
        calibrationDate: new Date('2023-03-10'),
        nextCalibrationDate: new Date('2024-03-10'),
        powerRequirements: '110V AC',
        safetyInstructions: 'Handle with care, use protective eyewear',
        maintenanceSchedule: {
          frequency: 90,
          lastMaintenance: new Date('2023-03-10'),
          nextMaintenance: new Date('2023-06-10'),
          maintenanceType: 'Regular cleaning and calibration',
        },
        isPortable: false,
        requiresTraining: true,
      },
    ],
    totalCount: 15,
    availableCount: 10,
    inUseCount: 3,
    maintenanceCount: 1,
    retiredCount: 1,
    totalValue: 37500.0,
    lastInventoryDate: new Date('2023-06-15'),
    department: 'Biology',
    location: 'Science Building',
    notes: 'Regular calibration required',
    name: 'Science Lab Equipment',
    description: 'Science lab equipment for science students',
    documents: {
      receipts: [mockDocument],
      invoices: [mockDocument],
      payments: [mockDocument],
    },
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: new Date(),
    quantity: 0,
    minimumQuantity: 2,
    cost: 2500.0,
    tags: ['lab equipment', 'science', 'microscopes'],
    actions: {
      created: mockUserAction,
    },
    assignedTo: { studentIds: ['child-1'], classIds: ['class-2'] },
  },
  {
    id: '3',
    materials: [
      {
        id: 'dr1',
        type: 'digital_resource',
        name: 'Interactive Physics Simulations',
        description:
          'Collection of physics simulations for high school students',
        category: MaterialCategory.DIGITAL_RESOURCE,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.NEW,
        purchaseDate: new Date('2023-02-01'),
        location: 'Digital Library',
        quantity: 1,
        minimumQuantity: 1,
        supplier: 'EduTech Solutions',
        cost: 299.99,
        tags: ['physics', 'simulation', 'digital'],
        actions: {
          created: mockUserAction,
        },
        resourceType: DigitalResourceType.SIMULATION,
        format: 'web-based',
        fileSize: 0,
        url: 'https://school.physics-simulations.edu',
        accessLevel: AccessLevel.STUDENT,
        licenseType: LicenseType.SUBSCRIPTION,
        expiryDate: new Date('2024-02-01'),
        compatibleDevices: ['Windows', 'MacOS', 'ChromeOS'],
        requiresInternet: true,
      },
    ],
    totalCount: 8,
    availableCount: 8,
    inUseCount: 0,
    maintenanceCount: 0,
    retiredCount: 0,
    totalValue: 2399.92,
    lastInventoryDate: new Date('2023-06-10'),
    department: 'Physics',
    location: 'Digital Resources',
    notes: 'Annual subscription renewal required',
    name: 'Science Digital Resources',
    description: 'Science digital resources for science students',
    documents: {
      receipts: [mockDocument],
      invoices: [mockDocument],
      payments: [mockDocument],
    },
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: new Date(),
    quantity: 0,
    minimumQuantity: 1,
    cost: 299.99,
    tags: ['digital', 'physics', 'simulations'],
    actions: {
      created: mockUserAction,
    },
    assignedTo: { classIds: ['class-1'] },
  },
  {
    id: '4',
    materials: [
      {
        id: 'st1',
        type: 'stationery',
        name: 'Premium Lab Notebooks',
        description: 'Bound laboratory notebooks for science experiments',
        category: MaterialCategory.STATIONERY,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.NEW,
        purchaseDate: new Date('2023-04-15'),
        location: 'Supply Room 102',
        quantity: 100,
        minimumQuantity: 20,
        supplier: 'Office Supplies Co',
        cost: 4.99,
        tags: ['notebook', 'lab', 'consumable'],
        actions: {
          created: mockUserAction,
        },
        stationeryType: StationeryType.NOTEBOOK,
        brand: 'LabPro',
        size: 'A4',
        isConsumable: true,
      },
    ],
    totalCount: 150,
    availableCount: 100,
    inUseCount: 50,
    maintenanceCount: 0,
    retiredCount: 0,
    totalValue: 748.5,
    lastInventoryDate: new Date('2023-06-20'),
    department: 'Science',
    location: 'Main Supply Room',
    notes: 'Re-order when quantity reaches 20',
    name: 'Science Stationery',
    description: 'Science stationery for science students',
    documents: {
      receipts: [mockDocument],
      invoices: [mockDocument],
      payments: [mockDocument],
    },
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: new Date(),
    quantity: 0,
    minimumQuantity: 20,
    cost: 4.99,
    tags: ['stationery', 'notebooks', 'consumables'],
    actions: {
      created: mockUserAction,
    },
  },
  {
    id: '5',
    materials: [
      {
        id: 'b2',
        type: 'book',
        name: 'Advanced Mathematics',
        description: 'Comprehensive mathematics textbook for advanced students',
        category: MaterialCategory.BOOK,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.GOOD,
        purchaseDate: new Date('2023-01-20'),
        location: 'Math Department Library',
        quantity: 8,
        minimumQuantity: 3,
        supplier: 'Math Publishers Inc',
        cost: 89.99,
        tags: ['mathematics', 'advanced', 'textbook'],
        actions: {
          created: mockUserAction,
        },
        isbn: '978-0987654321',
        author: 'Prof. John Davis',
        publisher: 'Math Publishers Inc',
        edition: '4th',
        publicationYear: 2023,
        subject: 'Mathematics',
        gradeLevel: '12th Grade',
        language: 'English',
        isDigital: true,
        digitalFormat: 'pdf',
        digitalUrl: 'https://school.digital-library.edu/math-advanced',
      },
    ],
    totalCount: 30,
    availableCount: 20,
    inUseCount: 8,
    maintenanceCount: 0,
    retiredCount: 2,
    totalValue: 2699.7,
    lastInventoryDate: new Date('2023-06-05'),
    department: 'Mathematics',
    location: 'Math Building',
    notes: 'Both physical and digital copies available',
    name: 'Mathematics Textbooks',
    description: 'Mathematics textbooks for mathematics students',
    documents: {
      receipts: [mockDocument],
      invoices: [mockDocument],
      payments: [mockDocument],
    },
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: new Date(),
    quantity: 0,
    minimumQuantity: 3,
    cost: 89.99,
    tags: ['textbooks', 'mathematics', 'books'],
    actions: {
      created: mockUserAction,
    },
  },
  {
    id: '6',
    materials: [
      {
        id: 'le2',
        type: 'lab_equipment',
        name: 'Chemical Analysis Kit',
        description:
          'Complete set of chemicals and equipment for basic chemical analysis',
        category: MaterialCategory.LAB_EQUIPMENT,
        status: MaterialStatus.AVAILABLE,
        condition: MaterialCondition.NEW,
        purchaseDate: new Date('2023-05-01'),
        location: 'Chemistry Lab 203',
        quantity: 5,
        minimumQuantity: 2,
        supplier: 'ChemSupply Co',
        cost: 599.99,
        tags: ['chemistry', 'lab kit', 'chemicals'],
        actions: {
          created: mockUserAction,
        },
        equipmentType: LabEquipmentType.CHEMICAL,
        modelNumber: 'CK-2023',
        manufacturer: 'ChemSupply',
        serialNumber: 'CS2023-005',
        safetyInstructions: 'Use in ventilated area, wear protective gear',
        maintenanceSchedule: {
          frequency: 30,
          lastMaintenance: new Date('2023-05-01'),
          nextMaintenance: new Date('2023-06-01'),
          maintenanceType: 'Chemical inventory check',
        },
        isPortable: true,
        requiresTraining: true,
      },
    ],
    totalCount: 20,
    availableCount: 15,
    inUseCount: 5,
    maintenanceCount: 0,
    retiredCount: 0,
    totalValue: 11999.8,
    lastInventoryDate: new Date('2023-06-18'),
    department: 'Chemistry',
    location: 'Chemistry Building',
    notes: 'Regular chemical inventory required',
    name: 'Chemistry Lab Equipment',
    description: 'Chemistry lab equipment for chemistry students',
    documents: {
      receipts: [mockDocument],
      invoices: [mockDocument],
      payments: [mockDocument],
    },
    category: MaterialCategory.BOOK,
    status: MaterialStatus.AVAILABLE,
    condition: MaterialCondition.NEW,
    purchaseDate: new Date(),
    quantity: 0,
    minimumQuantity: 2,
    cost: 599.99,
    tags: ['lab equipment', 'chemistry', 'chemicals'],
    actions: {
      created: mockUserAction,
    },
  },
];
